import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

/**
 * The record of every model call this app has made.
 *
 * **The list never carries the big fields.** `request`, `response` and `reasoning` are what make a
 * row large and what a table of timestamps never draws, so a page leaves them on the server and
 * sets `hasReasoning` instead — enough to mark which rows are worth opening. One row fetched by id
 * fills them in.
 *
 * **A tool-calling question is several rows, not one.** `correlation` ties the rounds of one
 * question together and `round` orders them; that difference between rounds is where the
 * interesting failures live, so it is not something to collapse away in the client either.
 */

export interface LlmLogEntry {
  id: string;
  createdAt: number;
  surface: string;
  model: string;
  correlation: string;
  round: number;
  logId: string | null;
  promptTokens: number;
  completionTokens: number;
  cachedTokens: number;
  durationMs: number;
  error: string | null;
  hasReasoning: boolean;
  request: unknown[];
  response: string | null;
  reasoning: string | null;
  toolCalls: unknown[];
}

export interface LlmLogPage {
  entries: LlmLogEntry[];
  total: number;
}

async function json<T>(path: string, init?: RequestInit, what = "read the model log"): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { cache: "no-store", ...init });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    const detail = await res
      .json()
      .then((b) => (b as { detail?: string }).detail)
      .catch(() => undefined);
    throw new Error(detail ?? `Could not ${what} (${res.status})`);
  }
  return res.json();
}

export function listLlmLogs(opts?: {
  limit?: number;
  offset?: number;
  surface?: string;
}): Promise<LlmLogPage> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  if (opts?.surface) params.set("surface", opts.surface);
  const query = params.toString();
  return json<LlmLogPage>(`/api/llm-logs${query ? `?${query}` : ""}`);
}

export function readLlmLog(id: string): Promise<LlmLogEntry> {
  return json<LlmLogEntry>(`/api/llm-logs/${id}`);
}

/** Delete every row. Answers with how many went, in `total`. */
export function clearLlmLogs(): Promise<LlmLogPage> {
  return json<LlmLogPage>("/api/llm-logs", { method: "DELETE" }, "clear the model log");
}
